/**
 * Page centrale de gestion des réductions commerciales
 * Permet de créer, modifier, supprimer et filtrer les remises
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Table, Button, Modal, Form, Input, Select, Switch, message, Space, Tag, Statistic, Row, Col, InputNumber, Grid, Spin, Empty, Pagination } from 'antd';
import { DatePicker } from 'antd';
import { PlusOutlined, PercentageOutlined, EuroOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import discountService from '../services/discountService';
import { creerFormateurMonetaire } from '../utils/devise';

const { RangePicker } = DatePicker;
const { Option } = Select;

// Formateur monétaire évalué à l'appel pour suivre la devise de la boutique active
const numberFormatter = { format: (valeur) => creerFormateurMonetaire().format(valeur) };

const Discounts = () => {
  const [loading, setLoading] = useState(false);
  const [types, setTypes] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, scheduled: 0 });
  const [filters, setFilters] = useState({ search: '', status: '', typeId: '' });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [modalVisible, setModalVisible] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [form] = Form.useForm();
  // Détection mobile (< md) : `=== false` évite un flash de cartes avant la mesure sur desktop
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;

  /**
   * Normalise les champs numériques pour éviter d'envoyer des 0 involontaires
   * @param {number|null|undefined} value - Valeur saisie dans les InputNumber
   */
  const normalizeNumericField = (value) => {
    if (value === undefined || value === null || value === '') {
      return null;
    }
    return Number(value);
  };

  /**
   * Rend un indicateur d'icône différent selon le type choisi
   */
  const renderTypeIcon = (typeCode) => {
    if (typeCode === 'PERCENTAGE') {
      return <PercentageOutlined className="text-blue-500 mr-1" />;
    }
    return <EuroOutlined className="text-green-500 mr-1" />;
  };

  /**
   * Charge les réductions en appliquant les filtres en cours
   */
  const loadDiscounts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await discountService.listDiscounts({
        ...filters,
        page: pagination.current,
        pageSize: pagination.pageSize
      });
      setDiscounts(response.discounts || []);
      setPagination(prev => ({ ...prev, total: response.total }));

      const now = dayjs();
      const active = response.discounts?.filter(d => d.is_active)?.length || 0;
      const scheduled = response.discounts?.filter(d => d.start_date && dayjs(d.start_date).isAfter(now))?.length || 0;
      setStats({
        total: response.total || 0,
        active,
        scheduled
      });
    } catch (error) {
      console.error('Erreur lors du chargement des réductions:', error);
      message.error(error.response?.data?.error || 'Erreur lors du chargement des réductions');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.current, pagination.pageSize]);

  /**
   * Prépare l'ouverture du modal de création
   */
  const openCreateModal = useCallback(() => {
    setEditingDiscount(null);
    form.resetFields();
    setModalVisible(true);
  }, [form]);

  /**
   * Prépare l'édition d'une réduction existante
   * Alimente le formulaire avec les données actuelles
   */
  const handleEdit = useCallback((record) => {
    setEditingDiscount(record);
    form.setFieldsValue({
      code: record.code,
      name: record.name,
      description: record.description,
      discount_type_id: record.discount_type_id,
      value: record.value,
      min_order_amount: record.min_order_amount,
      max_discount_amount: record.max_discount_amount,
      usage_limit: record.usage_limit,
      is_active: record.is_active,
      date_range: record.start_date || record.end_date ? [
        record.start_date ? dayjs(record.start_date) : null,
        record.end_date ? dayjs(record.end_date) : null
      ] : []
    });
    setModalVisible(true);
  }, [form]);

  /**
   * Déclenche la suppression d'une réduction après confirmation
   */
  const handleDelete = useCallback((record) => {
    Modal.confirm({
      // Conserve la portée du thème privé dans cette confirmation statique.
      rootClassName: "private-ui",
      title: `Supprimer ${record.name} ?`,
      content: 'Cette action est irréversible. Souhaitez-vous continuer ?',
      okText: 'Supprimer',
      okType: 'danger',
      cancelText: 'Annuler',
      onOk: async () => {
        try {
          await discountService.deleteDiscount(record.id);
          message.success('Réduction supprimée');
          loadDiscounts();
        } catch (error) {
          console.error('Erreur lors de la suppression:', error);
          message.error(error.response?.data?.error || 'Impossible de supprimer cette réduction');
        }
      }
    });
  }, [loadDiscounts]);

  /**
   * Soumet les données du formulaire vers l'API
   * Convertit correctement les nombres et les dates
   */
  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      setModalLoading(true);

      const payload = {
        name: values.name,
        code: values.code?.trim(),
        description: values.description || null,
        discount_type_id: values.discount_type_id,
        value: Number(values.value),
        min_order_amount: normalizeNumericField(values.min_order_amount),
        max_discount_amount: normalizeNumericField(values.max_discount_amount),
        usage_limit: normalizeNumericField(values.usage_limit),
        is_active: values.is_active,
        start_date: values.date_range?.[0] ? values.date_range[0].toISOString() : null,
        end_date: values.date_range?.[1] ? values.date_range[1].toISOString() : null
      };

      if (editingDiscount) {
        await discountService.updateDiscount(editingDiscount.id, payload);
        message.success('Réduction mise à jour');
      } else {
        await discountService.createDiscount(payload);
        message.success('Réduction créée');
      }

      setModalVisible(false);
      setEditingDiscount(null);
      form.resetFields();
      loadDiscounts();
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      console.error('Erreur lors de l\'enregistrement:', error);
      message.error(error.response?.data?.error || 'Impossible d\'enregistrer la réduction');
    } finally {
      setModalLoading(false);
    }
  }, [editingDiscount, form, loadDiscounts]);

  /**
   * Charge les types de réductions disponibles dès l'ouverture de la page
   * utile pour préparer les sélecteurs du formulaire
   */
  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const response = await discountService.getDiscountTypes();
        setTypes(response);
      } catch (error) {
        message.error('Impossible de charger les types de réductions');
      }
    };

    fetchTypes();
  }, []);

  /**
   * Recharge la liste des réductions chaque fois que les filtres ou la pagination changent
   * permet d'avoir une vue toujours à jour sans recharger la page
   */
  useEffect(() => {
    loadDiscounts();
  }, [loadDiscounts]);

  /**
   * Prépare les colonnes du tableau des réductions
   * useMemo évite de recalculer la structure si les dépendances ne changent pas
   */
  const columns = useMemo(() => [
    // Colonne pour afficher le code promo unique
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      render: (text) => (
        <span className="font-mono text-gray-900">{text}</span>
      )
    },
    {
      title: 'Nom',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <div className="font-semibold text-gray-900">{text}</div>
          {record.description && (
            <div className="text-xs text-gray-500">{record.description}</div>
          )}
        </div>
      )
    },
    {
      title: 'Type',
      dataIndex: ['discount_types', 'name'],
      key: 'type',
      render: (_, record) => (
        <Tag color={record.discount_types?.code === 'PERCENTAGE' ? 'blue' : 'green'}>
          {record.discount_types?.name || 'Type inconnu'}
        </Tag>
      )
    },
    {
      title: 'Valeur',
      dataIndex: 'value',
      key: 'value',
      render: (value, record) => (
        <span className="font-semibold text-gray-900">
          {record.discount_types?.code === 'PERCENTAGE' ? `${value}%` : numberFormatter.format(value)}
        </span>
      )
    },
    {
      title: 'Période',
      key: 'period',
      render: (_, record) => {
        if (!record.start_date && !record.end_date) {
          return <span className="text-gray-500">Toujours actif</span>;
        }

        return (
          <div className="text-sm text-gray-700">
            {record.start_date ? dayjs(record.start_date).format('DD/MM/YYYY') : 'Dès maintenant'}
            {' → '}
            {record.end_date ? dayjs(record.end_date).format('DD/MM/YYYY') : 'Sans fin'}
          </div>
        );
      }
    },
    {
      title: 'Conditions',
      key: 'conditions',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          {record.min_order_amount && (
            <span className="text-xs text-gray-600">
              Min commande: {numberFormatter.format(record.min_order_amount)}
            </span>
          )}
          {record.max_discount_amount && (
            <span className="text-xs text-gray-600">
              Max réduction: {numberFormatter.format(record.max_discount_amount)}
            </span>
          )}
          {record.usage_limit && (
            <span className="text-xs text-gray-600">
              Limite d'utilisation: {record.used_count}/{record.usage_limit}
            </span>
          )}
        </Space>
      )
    },
    {
      title: 'Statut',
      dataIndex: 'is_active',
      key: 'status',
      render: (isActive, record) => {
        const isScheduled = record.start_date && dayjs(record.start_date).isAfter(dayjs());
        return (
          <Tag color={isActive ? (isScheduled ? 'purple' : 'green') : 'red'}>
            {isActive ? (isScheduled ? 'Planifié' : 'Actif') : 'Inactif'}
          </Tag>
        );
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type="link" onClick={() => handleEdit(record)}>
            Modifier
          </Button>
          <Button type="link" danger onClick={() => handleDelete(record)}>
            Supprimer
          </Button>
        </Space>
      )
    }
  ], [handleDelete, handleEdit]);

  /**
   * Calcule les statistiques basiques à afficher en tête de page
   * useMemo évite les recalculs inutiles lors de rerender
   */
  const summaryStats = useMemo(() => ([
    { title: 'Réductions totales', value: stats.total },
    { title: 'Actives', value: stats.active },
    { title: 'Planifiées', value: stats.scheduled }
  ]), [stats]);

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Réductions</h1>
          <p className="text-gray-500">Créez des codes promo ou des remises planifiées pour booster vos ventes.</p>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal} className="w-full sm:w-auto">
          Nouvelle réduction
        </Button>
      </div>

      <Row gutter={[16, 16]}>
        {summaryStats.map(stat => (
          <Col xs={24} md={8} key={stat.title}>
            <Card>
              <Statistic title={stat.title} value={stat.value} />
            </Card>
          </Col>
        ))}
      </Row>

      <Card>
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <Input.Search
            placeholder="Rechercher une réduction"
            allowClear
            value={filters.search}
            onChange={(e) => {
              const value = e.target.value;
              setFilters(prev => ({ ...prev, search: value }));
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            onSearch={() => setPagination(prev => ({ ...prev, current: 1 }))}
            style={{ maxWidth: isMobile ? '100%' : 280 }}
          />
          <Select
            placeholder="Toutes les statuts"
            allowClear
            value={filters.status}
            onChange={(value) => {
              setFilters(prev => ({ ...prev, status: value || '' }));
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            style={{ width: isMobile ? '100%' : 200 }}
          >
            <Option value="active">Actives</Option>
            <Option value="inactive">Inactives</Option>
          </Select>
          <Select
            placeholder="Tous les types"
            allowClear
            value={filters.typeId}
            onChange={(value) => {
              setFilters(prev => ({ ...prev, typeId: value || '' }));
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            style={{ width: isMobile ? '100%' : 220 }}
          >
            {types.map((type) => (
              <Option value={type.id} key={type.id}>
                <span className="flex items-center">
                  {renderTypeIcon(type.code)}
                  {type.name}
                </span>
              </Option>
            ))}
          </Select>
        </div>

        {isMobile ? (
          /* Mobile : une carte par réduction, générée à partir des mêmes colonnes que le tableau */
          <Spin spinning={loading}>
            {discounts.length === 0 && !loading ? (
              <Empty description="Aucune réduction" />
            ) : (
              <div className="space-y-3">
                {discounts.map((record) => (
                  <div key={record.id} className="rounded-lg border border-gray-200 p-3 space-y-2">
                    {columns.filter((col) => col.key !== 'actions').map((col) => {
                      // Valeur de la cellule : dataIndex string ou tableau (chemin imbriqué)
                      const path = Array.isArray(col.dataIndex) ? col.dataIndex : [col.dataIndex];
                      const value = col.dataIndex ? path.reduce((acc, k) => acc?.[k], record) : undefined;
                      return (
                        <div key={col.key} className="flex items-start justify-between gap-3">
                          <span className="shrink-0 text-xs text-gray-500">{col.title}</span>
                          <div className="min-w-0 break-words text-right">
                            {col.render ? col.render(value, record) : value}
                          </div>
                        </div>
                      );
                    })}
                    {/* Actions en bas de carte */}
                    <div className="flex justify-end border-t border-gray-100 pt-2">
                      {columns.find((col) => col.key === 'actions')?.render(null, record)}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <Pagination
              simple
              className="mt-4 text-center"
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={pagination.total}
              showSizeChanger={false}
              onChange={(page, pageSize) => setPagination({ current: page, pageSize, total: pagination.total })}
            />
          </Spin>
        ) : (
          <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={discounts}
          scroll={{ x: 'max-content' }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            onChange: (page, pageSize) => setPagination({ current: page, pageSize, total: pagination.total })
          }}
        />
        )}
      </Card>

      <Modal
        title={editingDiscount ? `Modifier ${editingDiscount.name}` : 'Créer une réduction'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingDiscount(null);
        }}
        onOk={handleSubmit}
        confirmLoading={modalLoading}
        width={640}
        destroyOnClose
      >
        <Form layout="vertical" form={form} initialValues={{ is_active: true }}>
          {/* Champ de saisie du code promo partageable */}
          <Form.Item
            label="Code de réduction"
            name="code"
            rules={[
              { required: true, message: 'Veuillez saisir un code' },
              { min: 3, message: 'Le code doit contenir au moins 3 caractères' },
              { pattern: /^[A-Za-z0-9_-]+$/, message: 'Uniquement lettres, chiffres, tirets ou _' }
            ]}
          >
            <Input placeholder="Ex: RENTREE2025" />
          </Form.Item>
          <Form.Item
            label="Nom de la réduction"
            name="name"
            rules={[{ required: true, message: 'Veuillez saisir un nom' }]}
          >
            <Input placeholder="Ex: Promo rentrée 10%" />
          </Form.Item>

          <Form.Item label="Description (optionnelle)" name="description">
            <Input.TextArea rows={3} placeholder="Ajoutez un contexte pour votre équipe" />
          </Form.Item>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item
              label="Type de réduction"
              name="discount_type_id"
              rules={[{ required: true, message: 'Veuillez sélectionner un type' }]}
            >
              <Select placeholder="Choisir un type">
                {types.map((type) => (
                  <Option key={type.id} value={type.id}>
                    <span className="flex items-center">
                      {renderTypeIcon(type.code)}
                      {type.name}
                    </span>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Valeur"
              name="value"
              rules={[{ required: true, message: 'Veuillez saisir une valeur' }]}
            >
              <InputNumber
                min={0}
                step={0.1}
                precision={2}
                className="w-full"
                placeholder="Ex: 10 ou 5000"
              />
            </Form.Item>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item label="Montant minimum de commande" name="min_order_amount">
              <InputNumber min={0} className="w-full" placeholder="Ex: 10000" />
            </Form.Item>

            <Form.Item label="Réduction maximale" name="max_discount_amount">
              <InputNumber min={0} className="w-full" placeholder="Ex: 20000" />
            </Form.Item>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item label="Limite d'utilisation" name="usage_limit">
              <InputNumber min={1} className="w-full" placeholder="Ex: 100 utilisations" />
            </Form.Item>

            <Form.Item label="Période d'activation" name="date_range">
              <RangePicker className="w-full" format="DD/MM/YYYY" />
            </Form.Item>
          </div>

          <Form.Item
            label="Statut"
            name="is_active"
            valuePropName="checked"
          >
            <Switch checkedChildren="Actif" unCheckedChildren="Inactif" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Discounts;
